import React, { useEffect, useMemo, useState } from 'react';
import { X, FilePlus, Upload, Trash2, FileText, Download, CheckCircle2, Circle, AlertTriangle, ChevronDown, ChevronUp, Ban } from 'lucide-react';
import { apiService } from '../services/api';
import Modal from './Modal';
import { showGlobalNotification } from '../hooks/useNotifications';
import { useLanguage } from '../contexts/LanguageContext';

const PROVIDERS = ['Turkcell', 'Vodafone', 'Türk Telekom'];

const DOC_TYPES: { value: string; label: string; en: string }[] = [
  { value: 'line_contract', label: 'Hat Sözleşmesi', en: 'Line Contract' },
  { value: 'tax_plate', label: 'Vergi Levhası', en: 'Tax Certificate' },
  { value: 'power_of_attorney', label: 'Vekaletname', en: 'Power of Attorney' },
  { value: 'signature_circular', label: 'İmza Sirküleri', en: 'Signature Circular' },
  { value: 'trade_registry', label: 'Ticaret Sicil Gazetesi', en: 'Trade Registry Gazette' },
  { value: 'id_copy', label: 'Yetkili Kimlik Fotokopisi', en: 'Authorized Person ID Copy' },
  { value: 'other', label: 'Diğer', en: 'Other' },
];
// Talepte hazır olması beklenen temel evraklar
const REQUIRED_TYPES = ['line_contract', 'tax_plate', 'power_of_attorney', 'signature_circular'];

const STEPS: { value: string; label: string; en: string }[] = [
  { value: 'preparing', label: 'Evrak Hazırlığı', en: 'Preparing Documents' },
  { value: 'submitted', label: 'Başvuru Yapıldı', en: 'Application Submitted' },
  { value: 'processing', label: 'Operatörde İşlemde', en: 'Processing at Carrier' },
  { value: 'sim_delivered', label: 'SIM Teslim Alındı', en: 'SIM Received' },
  { value: 'activated', label: 'Hat Aktif', en: 'Line Active' },
];

const fmtSize = (n?: number) => !n ? '' : n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.ceil(n / 1024)} KB`;

const inputCls = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm';
const labelCls = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  companies: any[];
  tariffs: any[];
  locations: any[];
  users: any[];
  onLineActivated: () => void;
}

const GSMNewSubscription: React.FC<Props> = ({ isOpen, onClose, companies, tariffs, locations, users, onLineActivated }) => {
  const { language } = useLanguage();
  const en = language === 'en';
  const L = (tr: string, enText: string) => (en ? enText : tr);
  const locale = en ? 'en-US' : 'tr-TR';
  const docLabel = (v: string) => { const d = DOC_TYPES.find(x => x.value === v); return d ? (en ? d.en : d.label) : v; };
  const stepLabel = (v: string) => { const x = STEPS.find(st => st.value === v); return x ? (en ? x.en : x.label) : v; };
  const fmtDate = (d?: string) => d ? new Date(d).toLocaleString(locale, { dateStyle: 'short', timeStyle: 'short' }) : '';
  const [tab, setTab] = useState<'requests' | 'documents'>('requests');
  const [documents, setDocuments] = useState<any[]>([]);
  const [requests, setRequests] = useState<any[]>([]);
  const [docCompany, setDocCompany] = useState<number>(0);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [events, setEvents] = useState<any[]>([]);
  const [busy, setBusy] = useState(false);

  const [docForm, setDocForm] = useState({ doc_type: 'line_contract', title: '', expires_at: '' });
  const [docFile, setDocFile] = useState<File | null>(null);

  const [showNew, setShowNew] = useState(false);
  const [newReq, setNewReq] = useState({ company_id: 0, provider: PROVIDERS[0], line_count: 1, notes: '' });

  const [activating, setActivating] = useState<any | null>(null);
  const [act, setAct] = useState({ phone_number: '', tariff_id: 0, pin_code: '', puk_code: '', user_id: 0, location_id: 0 });

  const load = async () => {
    try {
      const [d, r] = await Promise.all([apiService.get('/gsm-subscriptions/documents'), apiService.get('/gsm-subscriptions/requests')]);
      setDocuments(d || []);
      setRequests(r || []);
    } catch (e: any) {
      showGlobalNotification.error(L('Hata', 'Error'), e?.message || L('Veriler yüklenemedi', 'Failed to load data'));
    }
  };

  useEffect(() => { if (isOpen) load(); }, [isOpen]);

  const loadEvents = async (id: number) => {
    try { setEvents(await apiService.get(`/gsm-subscriptions/requests/${id}/events`) || []); } catch { setEvents([]); }
  };

  const toggleExpand = (id: number) => {
    if (expanded === id) { setExpanded(null); return; }
    setExpanded(id);
    loadEvents(id);
  };

  const docsByCompany = useMemo(() => {
    const m: Record<number, any[]> = {};
    documents.forEach(d => { (m[d.company_id] ||= []).push(d); });
    return m;
  }, [documents]);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    try { await fn(); } catch (e: any) {
      showGlobalNotification.error(L('Hata', 'Error'), e?.message || L('İşlem başarısız', 'Operation failed'));
    } finally { setBusy(false); }
  };

  const uploadDoc = () => run(async () => {
    if (!docCompany) throw new Error(L('Önce firma seçin', 'Select a company first'));
    if (!docFile) throw new Error(L('Dosya seçin', 'Select a file'));
    const fd = new FormData();
    fd.append('company_id', String(docCompany));
    fd.append('doc_type', docForm.doc_type);
    if (docForm.title) fd.append('title', docForm.title);
    if (docForm.expires_at) fd.append('expires_at', docForm.expires_at);
    fd.append('file', docFile);
    await apiService.post('/gsm-subscriptions/documents', fd);
    setDocFile(null);
    setDocForm({ ...docForm, title: '', expires_at: '' });
    showGlobalNotification.success(L('Başarılı', 'Success'), L('Evrak yüklendi', 'Document uploaded'));
    await load();
  });

  const deleteDoc = (d: any) => {
    if (!window.confirm(L(`"${d.title}" evrakı silinsin mi?`, `Delete document "${d.title}"?`))) return;
    run(async () => { await apiService.delete(`/gsm-subscriptions/documents/${d.id}`); await load(); });
  };

  const createRequest = () => run(async () => {
    if (!newReq.company_id) throw new Error(L('Firma seçin', 'Select a company'));
    await apiService.post('/gsm-subscriptions/requests', { ...newReq, line_count: Number(newReq.line_count) || 1 });
    setShowNew(false);
    setNewReq({ company_id: 0, provider: PROVIDERS[0], line_count: 1, notes: '' });
    await load();
  });

  const setStatus = (r: any, status: string, note?: string) => run(async () => {
    await apiService.put(`/gsm-subscriptions/requests/${r.id}`, { status, event_note: note });
    await load();
    if (expanded === r.id) loadEvents(r.id);
  });

  const cancelRequest = (r: any) => {
    const reason = window.prompt(L('İptal nedeni (isteğe bağlı):', 'Cancellation reason (optional):'));
    if (reason === null) return;
    setStatus(r, 'cancelled', reason || undefined);
  };

  const deleteRequest = (r: any) => {
    if (!window.confirm(L('Talep kaydı silinsin mi?', 'Delete this request?'))) return;
    run(async () => { await apiService.delete(`/gsm-subscriptions/requests/${r.id}`); await load(); });
  };

  const activate = () => run(async () => {
    if (!act.phone_number.trim() || !act.tariff_id || !act.pin_code.trim() || !act.puk_code.trim()) {
      throw new Error(L('Numara, tarife, PIN ve PUK zorunludur', 'Number, tariff, PIN and PUK are required'));
    }
    await apiService.post(`/gsm-subscriptions/requests/${activating.id}/activate`, {
      phone_number: act.phone_number.trim(),
      tariff_id: act.tariff_id,
      pin_code: act.pin_code.trim(),
      puk_code: act.puk_code.trim(),
      user_id: act.user_id || null,
      location_id: act.location_id || null,
    });
    showGlobalNotification.success(L('Başarılı', 'Success'), L('Hat aktif edildi ve GSM listesine eklendi', 'Line activated and added to the GSM list'));
    setActivating(null);
    await load();
    onLineActivated();
  });

  const startActivate = (r: any) => {
    setAct({ phone_number: '', tariff_id: 0, pin_code: '', puk_code: '', user_id: 0, location_id: 0 });
    setActivating(r);
  };

  const docRow = (d: any, showDelete: boolean) => {
    const expired = d.expires_at && new Date(d.expires_at) < new Date(new Date().toDateString());
    return (
      <div key={d.id} className="flex items-center gap-3 py-2 px-3 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800">
        <FileText className="w-4 h-4 text-gray-500 flex-shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="text-sm text-gray-900 dark:text-gray-100 truncate">{d.title}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {docLabel(d.doc_type)} · {fmtSize(d.file_size)}
            {d.expires_at && <span className={expired ? 'text-red-600 font-medium' : ''}> · {L('Geçerlilik', 'Valid until')}: {new Date(d.expires_at).toLocaleDateString(locale)}{expired ? L(' (süresi dolmuş)', ' (expired)') : ''}</span>}
          </div>
        </div>
        <a href={d.file_path} target="_blank" rel="noreferrer" download={d.original_name}
          className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-gray-700 rounded" title={L('Aç / İndir', 'Open / Download')}>
          <Download className="w-4 h-4" />
        </a>
        {showDelete && (
          <button onClick={() => deleteDoc(d)} className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-gray-700 rounded" title={L('Sil', 'Delete')}>
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
    );
  };

  const checklist = (companyId: number) => {
    const have = new Set((docsByCompany[companyId] || []).map(d => d.doc_type));
    return (
      <div className="flex flex-wrap gap-2">
        {REQUIRED_TYPES.map(t => (
          <span key={t} className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full ${have.has(t)
            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
            : 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'}`}>
            {have.has(t) ? <CheckCircle2 className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
            {docLabel(t)}
          </span>
        ))}
      </div>
    );
  };

  const stepper = (r: any) => {
    const cancelled = r.status === 'cancelled';
    const idx = STEPS.findIndex(s => s.value === r.status);
    return (
      <div className="flex items-center gap-1 overflow-x-auto py-1">
        {STEPS.map((s, i) => {
          const done = !cancelled && i <= idx;
          return (
            <React.Fragment key={s.value}>
              <div className={`flex items-center gap-1 text-xs whitespace-nowrap ${done ? 'text-green-700 dark:text-green-400 font-medium' : 'text-gray-400'}`}>
                {done ? <CheckCircle2 className="w-4 h-4" /> : <Circle className="w-4 h-4" />}
                {en ? s.en : s.label}
              </div>
              {i < STEPS.length - 1 && <div className={`h-px w-4 flex-shrink-0 ${!cancelled && i < idx ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'}`} />}
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  const nextStep = (r: any) => {
    const i = STEPS.findIndex(s => s.value === r.status);
    return i >= 0 && i < STEPS.length - 2 ? STEPS[i + 1] : null; // son adım (Aktif) ayrı butonla
  };

  const companyDocs = docsByCompany[docCompany] || [];

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose}>
        <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-4xl">
          <div className="p-5 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FilePlus className="w-5 h-5 text-red-600" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{L('Yeni Abonelik', 'New Subscription')}</h2>
            </div>
            <button onClick={onClose} className="p-1 text-gray-500 hover:text-gray-700"><X className="w-5 h-5" /></button>
          </div>

          <div className="px-5 pt-3 flex gap-2 border-b border-gray-200 dark:border-gray-700">
            {[{ k: 'requests', l: `${L('Abonelik Talepleri', 'Subscription Requests')} (${requests.filter(r => !['activated', 'cancelled'].includes(r.status)).length})` }, { k: 'documents', l: L('Firma Evrakları', 'Company Documents') }].map(t => (
              <button key={t.k} onClick={() => setTab(t.k as any)}
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === t.k ? 'border-red-600 text-red-600' : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900'}`}>
                {t.l}
              </button>
            ))}
          </div>

          <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
            {tab === 'documents' && (
              <>
                <div>
                  <label className={labelCls}>{L('Firma', 'Company')}</label>
                  <select className={inputCls} value={docCompany} onChange={e => setDocCompany(parseInt(e.target.value))}>
                    <option value={0}>{L('-- Firma seçin --', '-- Select company --')}</option>
                    {companies.map(c => <option key={c.id} value={c.id}>{c.name} ({(docsByCompany[c.id] || []).length} {L('evrak', 'docs')})</option>)}
                  </select>
                </div>

                {docCompany > 0 && (
                  <>
                    {checklist(docCompany)}
                    <div className="border border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className={labelCls}>{L('Evrak Türü', 'Document Type')}</label>
                        <select className={inputCls} value={docForm.doc_type} onChange={e => setDocForm({ ...docForm, doc_type: e.target.value })}>
                          {DOC_TYPES.map(d => <option key={d.value} value={d.value}>{en ? d.en : d.label}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className={labelCls}>{L('Başlık (isteğe bağlı)', 'Title (optional)')}</label>
                        <input className={inputCls} value={docForm.title} onChange={e => setDocForm({ ...docForm, title: e.target.value })} placeholder={L('Boşsa dosya adı kullanılır', 'File name is used if empty')} />
                      </div>
                      <div>
                        <label className={labelCls}>{L('Geçerlilik Bitişi (isteğe bağlı)', 'Expiry Date (optional)')}</label>
                        <input type="date" className={inputCls} value={docForm.expires_at} onChange={e => setDocForm({ ...docForm, expires_at: e.target.value })} />
                      </div>
                      <div>
                        <label className={labelCls}>{L('Dosya (PDF, resim, Word · en fazla 15 MB)', 'File (PDF, image, Word · max 15 MB)')}</label>
                        <input type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" className="text-sm text-gray-700 dark:text-gray-300"
                          onChange={e => setDocFile(e.target.files?.[0] || null)} />
                      </div>
                      <div className="sm:col-span-2 flex justify-end">
                        <button onClick={uploadDoc} disabled={busy || !docFile}
                          className="flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 text-sm">
                          <Upload className="w-4 h-4 mr-2" /> {L('Yükle', 'Upload')}
                        </button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      {companyDocs.length === 0
                        ? <p className="text-sm text-gray-500 text-center py-4">{L('Bu firma için henüz evrak yüklenmemiş.', 'No documents uploaded for this company yet.')}</p>
                        : companyDocs.map(d => docRow(d, true))}
                    </div>
                  </>
                )}
              </>
            )}

            {tab === 'requests' && (
              <>
                {!showNew ? (
                  <div className="flex justify-end">
                    <button onClick={() => setShowNew(true)} className="flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm">
                      <FilePlus className="w-4 h-4 mr-2" /> {L('Yeni Talep', 'New Request')}
                    </button>
                  </div>
                ) : (
                  <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <label className={labelCls}>{L('Firma', 'Company')} *</label>
                      <select className={inputCls} value={newReq.company_id} onChange={e => setNewReq({ ...newReq, company_id: parseInt(e.target.value) })}>
                        <option value={0}>{L('-- Firma seçin --', '-- Select company --')}</option>
                        {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className={labelCls}>{L('Servis Sağlayıcı', 'Service Provider')} *</label>
                      <select className={inputCls} value={newReq.provider} onChange={e => setNewReq({ ...newReq, provider: e.target.value })}>
                        {PROVIDERS.map(p => <option key={p} value={p}>{p}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className={labelCls}>{L('Hat Adedi', 'Number of Lines')}</label>
                      <input type="number" min={1} className={inputCls} value={newReq.line_count} onChange={e => setNewReq({ ...newReq, line_count: parseInt(e.target.value) || 1 })} />
                    </div>
                    <div className="sm:col-span-3">
                      <label className={labelCls}>{L('Not', 'Note')}</label>
                      <input className={inputCls} value={newReq.notes} onChange={e => setNewReq({ ...newReq, notes: e.target.value })} placeholder={L('Örn: Yönetici için 1 adet kurumsal hat', 'e.g. 1 corporate line for the manager')} />
                    </div>
                    {newReq.company_id > 0 && <div className="sm:col-span-3">{checklist(newReq.company_id)}</div>}
                    <div className="sm:col-span-3 flex justify-end gap-2">
                      <button onClick={() => setShowNew(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm text-gray-700 dark:text-gray-300">{L('İptal', 'Cancel')}</button>
                      <button onClick={createRequest} disabled={busy} className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 text-sm">{L('Talebi Oluştur', 'Create Request')}</button>
                    </div>
                  </div>
                )}

                {requests.length === 0 && !showNew && <p className="text-sm text-gray-500 text-center py-8">{L('Henüz abonelik talebi yok.', 'No subscription requests yet.')}</p>}

                {requests.map(r => {
                  const closed = r.status === 'activated' || r.status === 'cancelled';
                  const next = nextStep(r);
                  const cDocs = docsByCompany[r.company_id] || [];
                  return (
                    <div key={r.id} className={`border rounded-lg p-4 ${closed ? 'border-gray-200 dark:border-gray-700 opacity-75' : 'border-gray-300 dark:border-gray-600'}`}>
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div>
                          <div className="font-medium text-gray-900 dark:text-gray-100">{r.company_name} · {r.provider}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {r.line_count} {L('hat', 'line(s)')} · {fmtDate(r.created_at)} · {r.created_by_name}
                            {r.phone_number && <> · <span className="font-medium text-green-700 dark:text-green-400">{r.phone_number}</span></>}
                          </div>
                          {r.notes && <div className="text-sm text-gray-600 dark:text-gray-300 mt-1">{r.notes}</div>}
                        </div>
                        {r.status === 'cancelled' && <span className="text-xs px-2 py-1 rounded-full bg-gray-200 text-gray-700">{L('İptal edildi', 'Cancelled')}</span>}
                      </div>

                      <div className="mt-2">{stepper(r)}</div>

                      <div className="mt-3 flex flex-wrap items-center gap-2">
                        {!closed && next && (
                          <button disabled={busy} onClick={() => setStatus(r, next.value)}
                            className="px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm disabled:opacity-50">
                            → {en ? next.en : next.label}
                          </button>
                        )}
                        {!closed && (
                          <button disabled={busy} onClick={() => startActivate(r)}
                            className="px-3 py-1.5 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm disabled:opacity-50">
                            {L('Hat Aktif Edildi', 'Line Activated')}
                          </button>
                        )}
                        {!closed && (
                          <button disabled={busy} onClick={() => cancelRequest(r)} className="px-3 py-1.5 text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md text-sm flex items-center">
                            <Ban className="w-3.5 h-3.5 mr-1" /> {L('İptal Et', 'Cancel Request')}
                          </button>
                        )}
                        {r.status !== 'activated' && closed && (
                          <button disabled={busy} onClick={() => deleteRequest(r)} className="px-3 py-1.5 text-red-600 border border-red-300 rounded-md text-sm">{L('Sil', 'Delete')}</button>
                        )}
                        <button onClick={() => toggleExpand(r.id)} className="ml-auto flex items-center text-sm text-gray-600 dark:text-gray-300">
                          {L('Evraklar ve geçmiş', 'Documents & history')} {expanded === r.id ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />}
                        </button>
                      </div>

                      {expanded === r.id && (
                        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 space-y-3">
                          <div>{checklist(r.company_id)}</div>
                          <div className="space-y-2">
                            {cDocs.length === 0
                              ? <p className="text-sm text-gray-500">{L('Bu firmaya ait evrak yok. "Firma Evrakları" sekmesinden yükleyebilirsiniz.', 'No documents for this company. Upload them from the "Company Documents" tab.')}</p>
                              : cDocs.map(d => docRow(d, false))}
                          </div>
                          <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                            {events.map(e => (
                              <div key={e.id}>
                                {fmtDate(e.created_at)} — <span className="font-medium">{e.status === 'cancelled' ? L('İptal', 'Cancelled') : stepLabel(e.status)}</span>
                                {e.note ? ` · ${e.note}` : ''}{e.user_name ? ` (${e.user_name})` : ''}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </div>
      </Modal>

      <Modal isOpen={!!activating} onClose={() => setActivating(null)} zIndex="z-[70]">
        <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-lg">
          <div className="p-5 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{L('Hat Aktif Edildi', 'Line Activated')}</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{activating?.company_name} · {activating?.provider} — {L('hat GSM listesine eklenecek.', 'the line will be added to the GSM list.')}</p>
          </div>
          <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>{L('GSM Numarası', 'GSM Number')} *</label>
              <input className={inputCls} value={act.phone_number} onChange={e => setAct({ ...act, phone_number: e.target.value })} placeholder="05XX XXX XX XX" />
            </div>
            <div>
              <label className={labelCls}>{L('Tarife', 'Tariff')} *</label>
              <select className={inputCls} value={act.tariff_id} onChange={e => setAct({ ...act, tariff_id: parseInt(e.target.value) })}>
                <option value={0}>{L('-- Seçin --', '-- Select --')}</option>
                {tariffs.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>PIN *</label>
              <input className={inputCls} maxLength={10} value={act.pin_code} onChange={e => setAct({ ...act, pin_code: e.target.value })} />
            </div>
            <div>
              <label className={labelCls}>PUK *</label>
              <input className={inputCls} maxLength={20} value={act.puk_code} onChange={e => setAct({ ...act, puk_code: e.target.value })} />
            </div>
            <div>
              <label className={labelCls}>{L('Kullanıcı (boşsa "Boş" olarak eklenir)', 'User (added as "Available" if empty)')}</label>
              <select className={inputCls} value={act.user_id} onChange={e => setAct({ ...act, user_id: parseInt(e.target.value) })}>
                <option value={0}>{L('-- Atanmamış --', '-- Unassigned --')}</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>{L('Lokasyon', 'Location')}</label>
              <select className={inputCls} value={act.location_id} onChange={e => setAct({ ...act, location_id: parseInt(e.target.value) })}>
                <option value={0}>{L('-- Seçin --', '-- Select --')}</option>
                {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </div>
          </div>
          <div className="p-5 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
            <button onClick={() => setActivating(null)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm text-gray-700 dark:text-gray-300">{L('Vazgeç', 'Back')}</button>
            <button onClick={activate} disabled={busy} className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 text-sm">{L('Aktif Et ve Listeye Ekle', 'Activate and Add to List')}</button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default GSMNewSubscription;
